import { useFetchBusinesses } from '@/composables/fetch/useFetchBusinesses'
import { usePostHog } from '@/composables/usePostHog'
import { useSentryEvents } from '@/composables/useSentryEvents'
import { useToast } from '@/features/shared/composables/useToast'
import { useTranslation } from '@/features/shared/composables/useTranslation'
import authApi from '@/services/calls/auth'
import { LoginPayload, SignupPayload } from '@/types/auth'
// Note: router is imported like this because the logout function of
// this composable is used in api.ts which is outside of the vue instance
import router from '@/router'

type LoginPayloadByType =
  | {
      type: 'default'
      email: string
      password: string
    }
  | {
      type: '2fa'
      token: string
      code: string
    }

export const useAuth = () => {
  const authStore = useAuthStore()
  const businessStore = useBusinessStore()
  const { fetchAllBusinesses } = useFetchBusinesses()
  const { setSentryUser } = useSentryEvents()
  const { posthog } = usePostHog()
  const { showToast } = useToast()
  const t = useTranslation()

  const login = async (payload: LoginPayload) => {
    try {
      const response = await authApi.login(payload)
      return response
    } catch (error) {
      console.log(error)
    }
  }

  const loginWith2Fa = async (payload: { token: string; code: string }) => {
    try {
      const response = await authApi.loginWith2Fa(payload)
      return response
    } catch (error) {
      console.log(error)
    }
  }

  const getUserMe = async () => {
    try {
      const response = await authApi.getUserMe()
      authStore.user = response
    } catch (error) {
      console.log(error)
    }
  }

  const handleLogin = async (payload: LoginPayloadByType, redirect?: string) => {
    try {
      const response = payload.type === 'default' ? await login(payload) : await loginWith2Fa(payload)

      if (response?.two_fa_required) {
        router.push({ name: 'TwoFactorAuth', query: { token: response.token, remaining: response.remaining_seconds } })
        return
      }

      if (response?.access) {
        authStore.token = response.access
      } else {
        return
      }

      await fetchAllBusinesses()

      if (!businessStore.businesses?.results.length) {
        router.push({ name: 'SignupBusiness' })
        return
      }

      businessStore.selectedBusiness = businessStore.businesses.results[0]

      await getUserMe()

      if (!authStore.user) {
        return
      }

      setSentryUser(authStore.user)
      posthog.identify(String(authStore.user.id), {
        email: authStore.user.email,
      })

      if (redirect) {
        router.push(redirect)
        return
      }

      if (businessStore.businesses.results.length === 1) {
        router.push({ name: 'Admin' })
        return
      }

      router.push({ name: 'SelectBusiness' })
    } catch (error) {
      console.log(error)
    }
  }

  const logout = async (redirectToLogin: boolean = true) => {
    if (redirectToLogin) {
      authStore.token = null
      // Use location.href for a clean logout that bypasses router guards
      window.location.href = '/login'
      setTimeout(() => {
        setSentryUser(null)
        posthog.reset()
        authStore.resetAllStores()
      }, 1000)
    } else {
      authStore.token = null
      setSentryUser(null)
      posthog.reset()
      authStore.resetAllStores()
    }
  }

  const handleJump = async (token: string, businessId: number) => {
    try {
      if (!token || !businessId) {
        logout()
        return
      }

      if (authStore.token) {
        logout(false)
      }

      authStore.token = token
      await fetchAllBusinesses()

      const businessToSelect = businessStore.businesses?.results.find((business) => business.id === Number(businessId))
      if (!businessToSelect) {
        // #604: surface the failure instead of a silent logout that just looks like "jump doesn't work".
        console.error('[handleJump] jumped business not found in fetched businesses', { businessId })
        showToast({ text: t('business_access_failed') }, 'error')
        logout()
        return
      }

      businessStore.selectedBusiness = businessToSelect

      await getUserMe()

      if (!authStore.user) {
        return
      }

      setSentryUser(authStore.user)
      posthog.identify(String(authStore.user.id), {
        email: authStore.user.email,
      })

      router.push({ name: 'Admin' })
    } catch (error) {
      // #604: surface a thrown failure (e.g. a failed businesses fetch) instead of failing silently.
      console.error('[handleJump] failed', error)
      showToast({ text: t('business_access_failed') }, 'error')
    }
  }

  const register = async (payload: SignupPayload) => {
    try {
      const response = await authApi.register(payload)
      return response
    } catch (error) {
      console.log(error)
    }
  }

  const acceptInvitation = async (token: string) => {
    try {
      const response = await authApi.acceptInvitation(token)
      return response
    } catch (error) {
      console.log(error)
    }
  }

  const handleInvitationAccept = async (options: {
    userExists: boolean
    token: string
    businessId: number
    payload?: SignupPayload
  }) => {
    const { userExists, payload, token, businessId } = options

    try {
      if (!userExists && payload) {
        const response = await register(payload)
        if (!response) return
        authStore.token = response.access
      }

      await acceptInvitation(token)

      await fetchAllBusinesses()

      const businessToSelect = businessStore.businesses?.results.find((business) => business.id === businessId)

      if (!businessToSelect) {
        // #604: surface the failure instead of a silent logout.
        console.error('[handleInvitationAccept] business not found in fetched businesses', { businessId })
        showToast({ text: t('business_access_failed') }, 'error')
        logout()
        return
      }

      businessStore.selectedBusiness = businessToSelect

      await getUserMe()

      if (!authStore.user) {
        return
      }

      setSentryUser(authStore.user)
      posthog.identify(String(authStore.user.id), {
        email: authStore.user.email,
      })

      router.push({ name: 'Admin' })
    } catch (error) {
      console.log(error)
    }
  }

  return { handleLogin, handleJump, handleInvitationAccept, getUserMe, logout }
}
